/**
 * ViewModeSelector — manual viewport-mode switch for hybrid projects.
 *
 * A project's viewport mode is normally fixed: it is derived once per case from
 * the case's assigned categories (B2). When a case assigns categories that
 * disagree on gating mode (e.g. a phase-dependent lesion alongside a
 * view-dependent one), this control lets the annotator switch which layout the
 * viewport uses. It renders ONLY for such hybrids (>1 available mode) and is
 * the sole writer of `projectGatingModeAtom` after the per-case seed — the mode
 * never auto-changes.
 *
 * Placed in the left sidebar directly above the window PRESETS.
 */

import React from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { availableGatingModesAtom, projectGatingModeAtom } from "../../atoms/gatingAtoms";
import { GATING_MODE, type GatingMode } from "../../types/gating";

/** Short labels for the segmented control. */
const MODE_LABEL: Record<GatingMode, string> = {
  [GATING_MODE.NONE]: "Default",
  [GATING_MODE.VIEW_DEPENDENT]: "Planes",
  [GATING_MODE.PHASE_DEPENDENT]: "Phases",
  [GATING_MODE.SLICE_DEPENDENT]: "Slice/MIP",
};

/** Hover-title descriptions of what each layout does. */
const MODE_TITLE: Record<GatingMode, string> = {
  [GATING_MODE.NONE]: "Default single-series layout",
  [GATING_MODE.VIEW_DEPENDENT]: "Axial / coronal / sagittal planes side by side",
  [GATING_MODE.PHASE_DEPENDENT]: "Contrast phases side by side",
  [GATING_MODE.SLICE_DEPENDENT]: "Axial with a synchronized MIP slab",
};

export const ViewModeSelector: React.FC = () => {
  const availableModes = useAtomValue(availableGatingModesAtom);
  const activeMode = useAtomValue(projectGatingModeAtom);
  const setActiveMode = useSetAtom(projectGatingModeAtom);

  // Single (or zero) mode → nothing to switch; the layout is fixed.
  if (availableModes.length <= 1) return null;

  return (
    <div className="px-2 py-2 shrink-0" data-testid="view-mode-selector">
      <div className="sidebar-text-label text-center mb-1.5">View</div>
      <div className="sidebar-card rounded-lg p-1.5">
        <div className="flex gap-1">
          {availableModes.map((mode) => (
            <button
              key={mode}
              onClick={() => setActiveMode(mode)}
              className={`
                flex-1 px-1.5 py-1 rounded text-[8px] font-medium transition-all duration-200
                ${
                  activeMode === mode
                    ? "bg-primary/25 text-signal-text-tint ring-1 ring-primary/30"
                    : "text-fg-muted hover:text-white hover:bg-white/10"
                }
              `}
              title={MODE_TITLE[mode]}
            >
              {MODE_LABEL[mode]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
